import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  BoxProps,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  keyframes,
  SimpleGrid,
  Text,
  VStack
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { FaApple, FaGooglePlay } from "react-icons/fa";
import { createVotingHref, getPlatformSettings, PlatformSettings } from "@lubbezposrednio-frontend/core";
import { Reveal } from "./Reveal";
import {
  FiActivity,
  FiArrowRight,
  FiAward,
  FiBell,
  FiBox,
  FiServer,
  FiBriefcase,
  FiCalendar,
  FiCheckCircle,
  FiEdit3,
  FiEyeOff,
  FiFileText,
  FiGift,
  FiGlobe,
  FiGrid,
  FiHome,
  FiImage,
  FiKey,
  FiLink,
  FiMessageSquare,
  FiPlus,
  FiSearch,
  FiShield,
  FiSmartphone,
  FiTag,
  FiUsers
} from "react-icons/fi";

const Section: React.FC<{ children: React.ReactNode; bg?: string; id?: string }> = ({ children, bg, id }) => (
  <Box as="section" id={id} w="100%" bg={bg}>
    <Reveal maxW="1180px" mx="auto" px={{ base: "20px", md: "40px", xl: "56px" }} py={{ base: "56px", md: "84px" }}>
      {children}
    </Reveal>
  </Box>
);

const SectionHead: React.FC<{ eyebrow?: string; title: string; desc?: string }> = ({ eyebrow, title, desc }) => (
  <VStack spacing="10px" mb={{ base: "36px", md: "52px" }} textAlign="center">
    {eyebrow && (
      <Text fontSize="13px" fontWeight="800" letterSpacing="0.08em" textTransform="uppercase" color="accent">
        {eyebrow}
      </Text>
    )}
    <Heading fontSize={{ base: "28px", md: "38px" }} fontWeight="800" color="primary" maxW="760px">
      {title}
    </Heading>
    {desc && (
      <Text fontSize={{ base: "15px", md: "17px" }} color="gray.500" maxW="640px">
        {desc}
      </Text>
    )}
  </VStack>
);

// ── JAK TO DZIAŁA ──
const STEPS = [
  { icon: FiEdit3, title: "Stwórz głosowanie", desc: "Kreator: pytania, próg ważności, tajność, typ elektoratu." },
  { icon: FiUsers, title: "Zbierz uprawnionych", desc: "Lista, zapisy (vox populi) albo weryfikacja tożsamości." },
  { icon: FiCheckCircle, title: "Głosowanie", desc: "Anonimowe, tajne lub jawne imienne — bez logowania." },
  { icon: FiLink, title: "Wynik na blockchainie", desc: "Protokół zakotwiczony i weryfikowalny przez każdego." }
];

export const HowItWorks: React.FC = () => (
  <Section bg="white">
    <SectionHead eyebrow="Jak to działa" title="Od pomysłu do wyniku w czterech krokach" />
    <Box position="relative">
      {/* linia łącząca kroki (desktop) */}
      <Box display={{ base: "none", lg: "block" }} position="absolute" top="36px" left="13%" right="13%" h="2px" bgGradient="linear(to-r, rgba(67,0,152,0.12), rgba(229,31,68,0.35), rgba(67,0,152,0.12))" />
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={{ base: "34px", lg: "24px" }} position="relative">
        {STEPS.map((s, i) => (
          <VStack key={s.title} spacing="16px" textAlign="center" align="center">
            <Flex w="72px" h="72px" borderRadius="full" bg="white" border="2px solid" borderColor="accent" align="center" justify="center" position="relative" boxShadow="0 10px 24px rgba(67,0,152,0.12)">
              <Icon as={s.icon} color="primary" boxSize="28px" />
              <Flex position="absolute" top="-7px" right="-7px" w="28px" h="28px" borderRadius="full" bg="accent" color="white" align="center" justify="center" fontSize="13px" fontWeight="800" border="3px solid white">
                {i + 1}
              </Flex>
            </Flex>
            <Heading fontSize="18px" fontWeight="800" color="primary">
              {s.title}
            </Heading>
            <Text fontSize="14px" color="gray.600" maxW="240px" lineHeight="1.55">
              {s.desc}
            </Text>
          </VStack>
        ))}
      </SimpleGrid>
    </Box>

    <Flex
      mt={{ base: "44px", md: "64px" }}
      direction={{ base: "column", md: "row" }}
      align="center"
      justify="space-between"
      gap="18px"
      bg="#F7F5FB"
      borderRadius="22px"
      px={{ base: "26px", md: "44px" }}
      py={{ base: "26px", md: "32px" }}
      textAlign={{ base: "center", md: "left" }}
    >
      <Box>
        <Heading fontSize={{ base: "19px", md: "23px" }} fontWeight="800" color="primary">
          Całość zajmuje kilka minut.
        </Heading>
        <Text fontSize="15px" color="gray.500" mt="3px">
          Bez technicznej wiedzy — kreator prowadzi Cię krok po kroku.
        </Text>
      </Box>
      <Button as={Link} to={createVotingHref()} bg="accent" color="white" borderRadius="12px" fontWeight="700" px="28px" py="14px" h="auto" rightIcon={<FiArrowRight />} flexShrink={0} boxShadow="0 8px 20px rgba(229,31,68,0.22)" _hover={{ bg: "#c41a3a" }}>
        Stwórz głosowanie
      </Button>
    </Flex>
  </Section>
);

// ── MANIFEST + WIDEO (przełamuje siatki kart) ──
const DECISIONS = [
  { icon: FiAward, label: "Wybór władz" },
  { icon: FiFileText, label: "Zmiana statutu" },
  { icon: FiImage, label: "Nowe logo" },
  { icon: FiCalendar, label: "Udział w wydarzeniu" },
  { icon: FiTag, label: "Tytuł wydarzenia" }
];

export const Manifesto: React.FC = () => (
  <Box as="section" w="100%" bgGradient="linear(165deg, #F4EFFF 0%, #FBF8FF 55%, #FFF1F4 100%)" position="relative" overflow="hidden">
    <Box position="absolute" top="-120px" left="-80px" w="420px" h="420px" borderRadius="full" bg="rgba(229,31,68,0.07)" filter="blur(80px)" />
    <Box position="absolute" bottom="-140px" right="-60px" w="420px" h="420px" borderRadius="full" bg="rgba(138,74,243,0.1)" filter="blur(80px)" />

    <Reveal position="relative" zIndex={1}>
    <VStack
      maxW="1080px"
      mx="auto"
      px={{ base: "20px", md: "40px" }}
      py={{ base: "60px", md: "92px" }}
      spacing={{ base: "30px", md: "40px" }}
      textAlign="center"
    >
      <Box>
        <Text fontSize="13px" fontWeight="800" letterSpacing="0.1em" textTransform="uppercase" color="accent" mb="16px">
          Demokracja bezpośrednia
        </Text>
        <Heading fontSize={{ base: "28px", md: "40px" }} fontWeight="800" lineHeight="1.12" letterSpacing="-0.01em" color="primary" maxW="720px" mx="auto">
          Najlepsze decyzje rodzą się,
          <br />
          gdy głos ma{" "}
          <Box as="span" color="accent">
            każdy.
          </Box>
        </Heading>
        <Text fontSize={{ base: "16px", md: "18px" }} lineHeight="1.6" color="gray.600" maxW="600px" mx="auto" mt="18px">
          Włącz członków swojej organizacji we wspólne decydowanie — od najważniejszych spraw po codzienne wybory.
        </Text>
      </Box>

      {/* Konkretne przykłady decyzji — energia + konkret */}
      <Flex wrap="wrap" justify="center" gap="10px">
        {DECISIONS.map(d => (
          <HStack key={d.label} spacing="8px" bg="white" border="1px solid rgba(67,0,152,0.1)" borderRadius="full" px="16px" py="9px" boxShadow="0 4px 12px rgba(67,0,152,0.05)">
            <Icon as={d.icon} boxSize="15px" color="accent" />
            <Text fontSize="14px" fontWeight="600" color="primary">
              {d.label}
            </Text>
          </HStack>
        ))}
      </Flex>

      {/* Wideo — centralny, duży, lekko przechylony */}
      <Box w="100%" maxW="780px">
        <Box
          position="relative"
          w="100%"
          pb="56.25%"
          borderRadius="22px"
          overflow="hidden"
          boxShadow="0 30px 70px rgba(67,0,152,0.22)"
          transform={{ md: "rotate(-1deg)" }}
          transition="transform 0.3s ease"
          _hover={{ transform: { md: "rotate(0deg) scale(1.01)" } }}
        >
          <Box
            as="iframe"
            src="https://www.youtube.com/embed/SQfFUkPust0"
            title="Instytut Demokracji Bezpośredniej"
            position="absolute"
            top="0"
            left="0"
            w="100%"
            h="100%"
            border="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </Box>
      </Box>

      <HStack spacing="16px" flexWrap="wrap" justify="center">
        <Button as={Link} to="/o-nas" variant="outline" borderColor="primary" color="primary" borderRadius="12px" fontWeight="700" px="26px" rightIcon={<FiArrowRight />} _hover={{ bg: "rgba(67,0,152,0.05)" }}>
          Poznaj ideę
        </Button>
        <Text fontSize="13px" fontWeight="700" color="gray.500">
          Instytut Demokracji Bezpośredniej
        </Text>
      </HStack>
    </VStack>
    </Reveal>
  </Box>
);

// ── DLA KOGO ──
const USE_CASES = [
  {
    icon: FiBriefcase,
    title: "Spółki i zarządy",
    desc: "Podejmuj wiążące decyzje korporacyjne z formalnym dowodem ważności — gotowym do dokumentacji.",
    examples: ["Uchwały zarządu", "Walne zgromadzenie", "Absolutorium"]
  },
  {
    icon: FiHome,
    title: "Wspólnoty i spółdzielnie",
    desc: "Uchwały mieszkańców z kworum i progiem przyjęcia — bez zbierania podpisów na korytarzu.",
    examples: ["Uchwały wspólnoty", "Plan remontów", "Wybór zarządcy"]
  },
  {
    icon: FiUsers,
    title: "Organizacje i NGO",
    desc: "Wybory władz i decyzje członkowskie — tajne lub jawne, z włączeniem nawet mniej aktywnych osób.",
    examples: ["Wybór władz", "Zmiana statutu", "Przyjęcie członków"]
  },
  {
    icon: FiGlobe,
    title: "Referenda",
    desc: "Otwarte głosowania ze zweryfikowaną tożsamością — dla całej społeczności, a nie zamkniętej listy.",
    examples: ["Konsultacje lokalne", "Inicjatywy mieszkańców", "Decyzje otwarte"]
  }
];

export const ForWhom: React.FC = () => {
  const [active, setActive] = useState(0);
  const u = USE_CASES[active];

  return (
    <Section bg="#F7F5FB">
      <SectionHead eyebrow="Dla kogo" title="Tam, gdzie liczy się wiarygodny wynik" />
      <Flex direction={{ base: "column", lg: "row" }} gap={{ base: "14px", lg: "28px" }} align="stretch">
        {/* Lewa — klikalne audytoria */}
        <VStack flex={{ lg: "0.85" }} align="stretch" spacing="8px">
          {USE_CASES.map((c, i) => (
            <Flex
              key={c.title}
              as="button"
              onClick={() => setActive(i)}
              align="center"
              gap="14px"
              p={{ base: "14px", md: "16px 18px" }}
              borderRadius="16px"
              textAlign="left"
              bg={i === active ? "white" : "transparent"}
              boxShadow={i === active ? "0 12px 28px rgba(67,0,152,0.1)" : "none"}
              transition="all 0.18s"
              _hover={{ bg: i === active ? "white" : "rgba(67,0,152,0.04)" }}
            >
              <Flex w="46px" h="46px" borderRadius="13px" flexShrink={0} align="center" justify="center" bg={i === active ? "accent" : "rgba(67,0,152,0.06)"} transition="all 0.18s">
                <Icon as={c.icon} color={i === active ? "white" : "primary"} boxSize="21px" />
              </Flex>
              <Text flex="1" fontSize={{ base: "16px", md: "17px" }} fontWeight="800" color="primary">
                {c.title}
              </Text>
              <Icon as={FiArrowRight} color="accent" boxSize="18px" opacity={i === active ? 1 : 0} transition="opacity 0.18s" />
            </Flex>
          ))}
        </VStack>

        {/* Prawa — panel szczegółów aktywnego audytorium (fioletowy, równoważy paletę) */}
        <Box flex={{ lg: "1.25" }} bgGradient="linear(150deg, #430098 0%, #6a1fc2 60%, #8b2fd6 100%)" borderRadius="24px" p={{ base: "28px", md: "44px" }} boxShadow="0 20px 50px rgba(67,0,152,0.28)" position="relative" overflow="hidden" color="white">
          <Box position="absolute" top="-70px" right="-50px" w="260px" h="260px" borderRadius="full" bgGradient="radial(circle, rgba(229,31,68,0.28), transparent 70%)" />
          <Flex w="62px" h="62px" borderRadius="18px" bg="rgba(255,255,255,0.15)" align="center" justify="center" mb="22px" position="relative">
            <Icon as={u.icon} color="white" boxSize="29px" />
          </Flex>
          <Heading fontSize={{ base: "24px", md: "30px" }} fontWeight="800" mb="12px" position="relative">
            {u.title}
          </Heading>
          <Text fontSize={{ base: "16px", md: "18px" }} color="rgba(255,255,255,0.88)" lineHeight="1.6" mb="28px" maxW="500px" position="relative">
            {u.desc}
          </Text>
          <Text fontSize="12px" fontWeight="800" letterSpacing="0.06em" textTransform="uppercase" color="rgba(255,255,255,0.6)" mb="12px" position="relative">
            Przykładowe głosowania
          </Text>
          <Flex wrap="wrap" gap="10px" position="relative">
            {u.examples.map(ex => (
              <HStack key={ex} bg="rgba(255,255,255,0.12)" border="1px solid rgba(255,255,255,0.16)" borderRadius="full" px="15px" py="9px" spacing="8px">
                <Icon as={FiCheckCircle} color="#ff9bb0" boxSize="15px" />
                <Text fontSize="14px" fontWeight="600">
                  {ex}
                </Text>
              </HStack>
            ))}
          </Flex>
        </Box>
      </Flex>
    </Section>
  );
};

// ── DLACZEGO ZAUFAĆ ──
const TRUST = [
  { icon: FiCheckCircle, title: "Weryfikowalność", desc: "Każdy może niezależnie sprawdzić, że wynik nie został zmieniony." },
  { icon: FiEyeOff, title: "Anonimowość", desc: "Tajność głosowania — uczestnictwo bez ujawniania wyboru." },
  { icon: FiLink, title: "Blockchain", desc: "Protokół zakotwiczony łańcuchem — odporny na manipulację." },
  { icon: FiShield, title: "Weryfikacja tożsamości", desc: "Opcjonalne potwierdzenie tożsamości (mObywatel) dla referendów." },
  { icon: FiGift, title: "Za darmo", desc: "Tworzysz i prowadzisz głosowania bez opłat." }
];

export const TrustSection: React.FC = () => (
  <Section bg="white">
    <Flex direction={{ base: "column", lg: "row" }} gap={{ base: "40px", lg: "72px" }} align="flex-start">
      {/* Lewa — manifest + CTA, „przykleja się" na desktopie */}
      <Box flex={{ lg: "0.85" }} position={{ lg: "sticky" }} top="110px">
        <Text fontSize="13px" fontWeight="800" letterSpacing="0.08em" textTransform="uppercase" color="accent" mb="14px">
          Dlaczego zaufać
        </Text>
        <Heading fontSize={{ base: "30px", md: "40px" }} fontWeight="800" lineHeight="1.12" color="primary" mb="16px">
          Zaufanie wbudowane w technologię, nie w obietnice
        </Heading>
        <Text fontSize={{ base: "16px", md: "17px" }} color="gray.500" lineHeight="1.6" mb="26px">
          Nie prosimy, byś nam wierzył — poprawność wyniku możesz sprawdzić samodzielnie, krok po kroku.
        </Text>
        <Button as={Link} to="/jak-zaglosowac" variant="outline" borderColor="primary" color="primary" borderRadius="12px" fontWeight="700" rightIcon={<FiArrowRight />} _hover={{ bg: "rgba(67,0,152,0.05)" }}>
          Jak to działa
        </Button>
      </Box>

      {/* Prawa — numerowana lista filarów */}
      <VStack flex={{ lg: "1.15" }} align="stretch" spacing="0" w="100%">
        {TRUST.map((t, i) => (
          <Flex
            key={t.title}
            align="flex-start"
            gap={{ base: "16px", md: "22px" }}
            py={{ base: "20px", md: "26px" }}
            borderTop={i === 0 ? "none" : "1px solid rgba(67,0,152,0.08)"}
            role="group"
          >
            <Text fontSize={{ base: "22px", md: "26px" }} fontWeight="900" color="rgba(67,0,152,0.18)" w={{ base: "34px", md: "44px" }} flexShrink={0} lineHeight="1" transition="color 0.18s" _groupHover={{ color: "accent" }}>
              0{i + 1}
            </Text>
            <Flex w="44px" h="44px" borderRadius="13px" bg="#F7F5FB" align="center" justify="center" flexShrink={0} transition="background 0.18s" _groupHover={{ bg: "accent" }}>
              <Icon as={t.icon} color="accent" boxSize="21px" transition="color 0.18s" _groupHover={{ color: "white" }} />
            </Flex>
            <Box pt="2px">
              <Heading fontSize={{ base: "17px", md: "19px" }} fontWeight="800" color="primary" mb="4px">
                {t.title}
              </Heading>
              <Text fontSize={{ base: "14px", md: "15px" }} color="gray.600" lineHeight="1.55">
                {t.desc}
              </Text>
            </Box>
          </Flex>
        ))}
      </VStack>
    </Flex>
  </Section>
);

// ── FAQ ──
const FAQS = [
  { q: "Czy muszę mieć konto, żeby zagłosować?", a: "Nie. Głosujesz z linku od organizatora albo zapisując się do głosowania otwartego — bez logowania." },
  { q: "Na czym polega zakotwiczenie na blockchainie?", a: "Po zakończeniu głosowania jego protokół jest hashowany i zapisywany w łańcuchu, dzięki czemu każdej zmiany wyniku nie da się ukryć." },
  { q: "Czy głosowanie jest tajne?", a: "Możesz wybrać: tajne (bez powiązania głosu z osobą) albo jawne imienne — zależnie od potrzeb." },
  { q: "Ile to kosztuje?", a: "Tworzenie i prowadzenie głosowań jest darmowe." }
];

export const FaqPreview: React.FC = () => (
  <Section bg="#F7F5FB">
    <SectionHead eyebrow="FAQ" title="Najczęstsze pytania" />
    <Box maxW="760px" mx="auto">
      <Accordion allowToggle>
        {FAQS.map(f => (
          <AccordionItem key={f.q} border="none" mb="12px" bg="white" borderRadius="16px" overflow="hidden" boxShadow="0 4px 14px rgba(67,0,152,0.05)">
            <AccordionButton py="18px" px="22px" _hover={{ bg: "rgba(67,0,152,0.02)" }}>
              <Box flex="1" textAlign="left" fontWeight="700" color="primary" fontSize="16px">
                {f.q}
              </Box>
              <AccordionIcon color="accent" />
            </AccordionButton>
            <AccordionPanel px="22px" pb="20px" color="gray.600" fontSize="15px" lineHeight="1.6">
              {f.a}
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
      <Flex justify="center" mt="28px">
        <Button as={Link} to="/faq" variant="outline" borderColor="primary" color="primary" borderRadius="12px" fontWeight="700" rightIcon={<FiArrowRight />} _hover={{ bg: "rgba(67,0,152,0.05)" }}>
          Wszystkie pytania
        </Button>
      </Flex>
    </Box>
  </Section>
);

// ── WNIOSEK O API ──
export const ApiCTA: React.FC = () => (
  <Section bg="white">
    <Flex
      direction={{ base: "column", lg: "row" }}
      align="center"
      justify="space-between"
      gap="28px"
      bgGradient="linear(135deg, #430098 0%, #6a1fc2 100%)"
      borderRadius="26px"
      p={{ base: "32px", md: "48px" }}
      color="white"
    >
      <Box maxW="640px">
        <HStack spacing="10px" mb="14px">
          <Flex w="44px" h="44px" borderRadius="13px" bg="rgba(255,255,255,0.15)" align="center" justify="center">
            <Icon as={FiKey} boxSize="22px" />
          </Flex>
          <Text fontSize="13px" fontWeight="800" letterSpacing="0.08em" textTransform="uppercase" opacity={0.85}>
            Dla integratorów
          </Text>
        </HStack>
        <Heading fontSize={{ base: "26px", md: "32px" }} fontWeight="800" mb="10px">
          Integruj głosowania przez API
        </Heading>
        <Text fontSize={{ base: "15px", md: "17px" }} opacity={0.88} lineHeight="1.6">
          Twórz i obsługuj głosowania z własnego systemu. Dostęp do API i dokumentacji przyznajemy na wniosek — bez osobnego panelu klienta.
        </Text>
      </Box>
      <Button as={Link} to="/rejestracja" bg="white" color="primary" borderRadius="12px" fontWeight="800" px="28px" py="14px" h="auto" flexShrink={0} rightIcon={<FiArrowRight />} _hover={{ bg: "rgba(255,255,255,0.9)" }}>
        Złóż wniosek o API
      </Button>
    </Flex>
  </Section>
);

// ── WPISY Z lubbezposrednio.pl (WordPress REST API, pobierane w przeglądarce) ──
type WpPost = {
  id: number;
  link: string;
  date: string;
  title: { rendered: string };
  _embedded?: { "wp:featuredmedia"?: { source_url?: string }[] };
};

const formatPostDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" });
};

// dekoduje encje HTML (np. &#8211; → –) z tytułów WordPressa
const decodeEntities = (s: string) => {
  if (typeof document === "undefined") return s;
  const el = document.createElement("textarea");
  el.innerHTML = s;
  return el.value;
};

export const BlogFeed: React.FC = () => {
  const [posts, setPosts] = useState<WpPost[]>([]);

  useEffect(() => {
    fetch("https://instytut.lubbezposrednio.pl/wp-json/wp/v2/posts?per_page=3&_embed=1")
      .then(r => (r.ok ? r.json() : []))
      .then(data => Array.isArray(data) && setPosts(data as WpPost[]))
      .catch(() => undefined);
  }, []);

  if (!posts.length) return null;

  return (
    <Section bg="#F7F5FB">
      <Flex justify="space-between" align="flex-end" mb={{ base: "28px", md: "40px" }} flexWrap="wrap" gap="16px">
        <Box>
          <Text fontSize="13px" fontWeight="800" letterSpacing="0.08em" textTransform="uppercase" color="accent" mb="10px">
            Z bloga
          </Text>
          <Heading fontSize={{ base: "26px", md: "36px" }} fontWeight="800" color="primary">
            Wiedza o demokracji bezpośredniej
          </Heading>
        </Box>
        <Button as="a" href="https://instytut.lubbezposrednio.pl" target="_blank" variant="outline" borderColor="primary" color="primary" borderRadius="12px" fontWeight="700" rightIcon={<FiArrowRight />} _hover={{ bg: "rgba(67,0,152,0.05)" }}>
          Więcej na instytut.lubbezposrednio.pl
        </Button>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing="22px">
        {posts.map(p => {
          const img = p._embedded?.["wp:featuredmedia"]?.[0]?.source_url;
          return (
            <Box
              key={p.id}
              as="a"
              href={p.link}
              target="_blank"
              rel="noopener noreferrer"
              bg="white"
              borderRadius="20px"
              overflow="hidden"
              border="1px solid rgba(67,0,152,0.08)"
              boxShadow="0 8px 24px rgba(67,0,152,0.06)"
              role="group"
              transition="all 0.2s"
              _hover={{ transform: "translateY(-5px)", boxShadow: "0 20px 42px rgba(67,0,152,0.13)" }}
            >
              <Box h="190px" overflow="hidden" bgGradient="linear(135deg, #430098, #8b2fd6)">
                {img && (
                  <Image src={img} alt="" w="100%" h="100%" objectFit="cover" transition="transform 0.4s ease" _groupHover={{ transform: "scale(1.06)" }} />
                )}
              </Box>
              <Box p="22px">
                <Text fontSize="13px" fontWeight="600" color="gray.400" mb="8px">
                  {formatPostDate(p.date)}
                </Text>
                <Heading fontSize="17px" fontWeight="800" color="primary" lineHeight="1.35" noOfLines={3} minH="68px">
                  {decodeEntities(p.title.rendered)}
                </Heading>
                <HStack spacing="6px" color="accent" fontWeight="700" fontSize="14px" mt="14px">
                  <Text>Czytaj</Text>
                  <Icon as={FiArrowRight} boxSize="15px" transition="transform 0.2s" _groupHover={{ transform: "translateX(4px)" }} />
                </HStack>
              </Box>
            </Box>
          );
        })}
      </SimpleGrid>
    </Section>
  );
};

// ── APLIKACJA MOBILNA (sterowana z panelu /admin → Ustawienia platformy) ──
// Współdzielony, jednorazowy fetch ustawień — teaser „nad filmem" i pełna sekcja niżej robią 1 request.
let platformSettingsPromise: Promise<PlatformSettings | null> | null = null;
const fetchPlatformSettingsOnce = () => {
  if (!platformSettingsPromise) {
    platformSettingsPromise = getPlatformSettings()
      .then(r => r.data)
      .catch(() => null);
  }
  return platformSettingsPromise;
};

const usePlatformSettings = () => {
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  useEffect(() => {
    let active = true;
    fetchPlatformSettingsOnce().then(s => active && setSettings(s));
    return () => {
      active = false;
    };
  }, []);
  return settings;
};

const StoreButton: React.FC<{ icon: React.ElementType; top: string; bottom: string; href?: string }> = ({ icon, top, bottom, href }) => {
  const soon = !href;
  return (
    <Button
      as="a"
      href={href}
      target={soon ? undefined : "_blank"}
      rel={soon ? undefined : "noopener noreferrer"}
      bg={soon ? "rgba(255,255,255,0.10)" : "black"}
      color="white"
      border={soon ? "1px solid rgba(255,255,255,0.28)" : "none"}
      h="58px"
      px="22px"
      borderRadius="14px"
      cursor={soon ? "default" : "pointer"}
      pointerEvents={soon ? "none" : "auto"}
      _hover={soon ? {} : { bg: "#1a1a1a", transform: "translateY(-2px)", textDecoration: "none" }}
      transition="all 0.15s"
    >
      <HStack spacing="12px">
        <Icon as={icon} boxSize="27px" opacity={soon ? 0.8 : 1} />
        <VStack spacing="1px" align="flex-start">
          <Text fontSize="10.5px" fontWeight="600" opacity={0.85} lineHeight="1" textTransform="uppercase" letterSpacing="0.03em">
            {soon ? "Już wkrótce" : top}
          </Text>
          <Text fontSize="18px" fontWeight="800" lineHeight="1.15">
            {bottom}
          </Text>
        </VStack>
      </HStack>
    </Button>
  );
};

// Dwa niezależnie pływające telefony (iPhone + Android) — każdy realny mockup albo placeholder z mini-kartą głosowania.
const floatPhone = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-18px); }
`;
const floatOrnament = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(0, -22px) scale(1.1); }
`;

type VoteRowData = { label: string; pct: number; color: string };

const VoteBar: React.FC<VoteRowData> = ({ label, pct, color }) => (
  <Box>
    <Flex justify="space-between" mb="3px">
      <Text fontSize={{ base: "8.5px", md: "10px" }} color="rgba(255,255,255,0.85)" fontWeight="600">
        {label}
      </Text>
      <Text fontSize={{ base: "8.5px", md: "10px" }} color="white" fontWeight="800">
        {pct}%
      </Text>
    </Flex>
    <Box w="100%" h={{ base: "5px", md: "6px" }} bg="rgba(255,255,255,0.14)" borderRadius="full" overflow="hidden">
      <Box w={`${pct}%`} h="100%" bg={color} borderRadius="full" />
    </Box>
  </Box>
);

// Mini-karta głosowania pokazywana na ekranie placeholdera (pod logo).
const MiniVoteCard: React.FC<{ title: string; rows: VoteRowData[] }> = ({ title, rows }) => (
  <Box w="100%" bg="rgba(255,255,255,0.10)" border="1px solid rgba(255,255,255,0.18)" borderRadius="14px" p={{ base: "10px", md: "13px" }}>
    <HStack spacing="6px" mb="7px">
      <Box w="7px" h="7px" borderRadius="full" bg="#2fce25" />
      <Text fontSize={{ base: "7.5px", md: "9px" }} fontWeight="800" letterSpacing="0.05em" textTransform="uppercase" color="rgba(255,255,255,0.65)">
        Trwa głosowanie
      </Text>
    </HStack>
    <Text fontSize={{ base: "10px", md: "12px" }} fontWeight="800" color="white" mb="9px" noOfLines={2} lineHeight="1.25">
      {title}
    </Text>
    <VStack spacing="7px" align="stretch">
      {rows.map(r => (
        <VoteBar key={r.label} {...r} />
      ))}
    </VStack>
  </Box>
);

const PhoneShell: React.FC<{ children?: React.ReactNode; transform?: Record<string, string>; bgGradient?: string }> = ({ children, transform, bgGradient }) => (
  <Box
    w={{ base: "150px", md: "212px" }}
    h={{ base: "310px", md: "440px" }}
    borderRadius={{ base: "28px", md: "38px" }}
    bgGradient={bgGradient ?? "linear(160deg, #1a0440, #2d0066)"}
    border="2px solid rgba(255,255,255,0.20)"
    boxShadow="0 34px 80px rgba(0,0,0,0.55)"
    position="relative"
    display="flex"
    flexDirection="column"
    alignItems="stretch"
    p={{ base: "20px 14px", md: "30px 18px" }}
    transform={transform}
  >
    <Box position="absolute" top="11px" left="50%" transform="translateX(-50%)" w="34%" h="6px" bg="rgba(255,255,255,0.28)" borderRadius="full" />
    {children}
  </Box>
);

// Mini-donut (frekwencja) — czysty CSS conic-gradient.
const MiniDonut: React.FC<{ pct: number; label: string; sub: string; color: string }> = ({ pct, label, sub, color }) => (
  <Flex align="center" gap="9px">
    <Box
      position="relative"
      w={{ base: "38px", md: "50px" }}
      h={{ base: "38px", md: "50px" }}
      borderRadius="full"
      flexShrink={0}
      style={{ background: `conic-gradient(${color} ${pct}%, rgba(255,255,255,0.14) 0)` }}
    >
      <Flex position="absolute" inset="22%" borderRadius="full" bg="#1d0840" align="center" justify="center">
        <Text fontSize={{ base: "8.5px", md: "10.5px" }} fontWeight="800" color="white">
          {pct}%
        </Text>
      </Flex>
    </Box>
    <Box>
      <Text fontSize={{ base: "9px", md: "11px" }} fontWeight="800" color="white" lineHeight="1.15">
        {label}
      </Text>
      <Text fontSize={{ base: "7.5px", md: "9px" }} color="rgba(255,255,255,0.6)">
        {sub}
      </Text>
    </Box>
  </Flex>
);

const ScreenChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box flex="1" bg="rgba(255,255,255,0.08)" border="1px solid rgba(255,255,255,0.14)" borderRadius="8px" px="8px" py="5px">
    <Text fontSize={{ base: "8px", md: "9.5px" }} fontWeight="700" color="rgba(255,255,255,0.9)" noOfLines={1} textAlign="center">
      {children}
    </Text>
  </Box>
);

type PhoneScreenData = {
  card: { title: string; rows: VoteRowData[] };
  donut: { pct: number; label: string; sub: string; color: string };
  stats: [string, string];
  cta: string;
};

// Pojedynczy pływający telefon: realny mockup (jeśli wgrany) albo bogaty placeholder-ekran aplikacji.
export const FloatingPhone: React.FC<{
  imageUrl: string | null;
  alt: string;
  transform: Record<string, string>;
  zIndex: number;
  duration: string;
  delay: string;
  bgGradient?: string;
  screen: PhoneScreenData;
}> = ({ imageUrl, alt, transform, zIndex, duration, delay, bgGradient, screen }) => (
  <Box position="relative" zIndex={zIndex} animation={`${floatPhone} ${duration} ease-in-out infinite`} sx={{ animationDelay: delay }}>
    {imageUrl ? (
      <Image src={imageUrl} alt={alt} transform={transform} maxH={{ base: "330px", md: "560px" }} w="auto" objectFit="contain" filter="drop-shadow(0 40px 80px rgba(0,0,0,0.55))" />
    ) : (
      <PhoneShell transform={transform} bgGradient={bgGradient}>
        <VStack spacing={{ base: "8px", md: "11px" }} w="100%" align="stretch">
          <Image src="/LBP_Logo_White.svg" alt="" maxH={{ base: "16px", md: "23px" }} objectFit="contain" alignSelf="center" opacity={0.9} mt={{ base: "2px", md: "4px" }} />
          <MiniVoteCard title={screen.card.title} rows={screen.card.rows} />
          <MiniDonut pct={screen.donut.pct} label={screen.donut.label} sub={screen.donut.sub} color={screen.donut.color} />
          <HStack spacing="6px" display={{ base: "none", md: "flex" }}>
            <ScreenChip>{screen.stats[0]}</ScreenChip>
            <ScreenChip>{screen.stats[1]}</ScreenChip>
          </HStack>
          <Box bg="white" borderRadius="11px" py={{ base: "7px", md: "9px" }} textAlign="center" boxShadow="0 6px 16px rgba(0,0,0,0.25)">
            <Text fontSize={{ base: "9.5px", md: "11.5px" }} fontWeight="800" color="#430098">
              {screen.cta}
            </Text>
          </Box>
        </VStack>
      </PhoneShell>
    )}
  </Box>
);

// Pływający szklany kafelek z ikoną (portfel, głosowanie, społeczność, blockchain) — obok telefonów.
const FloatBadge: React.FC<{
  icon: React.ElementType;
  pos: Record<string, string>;
  duration: string;
  delay: string;
}> = ({ icon, pos, duration, delay }) => (
  <Flex
    position="absolute"
    {...pos}
    w={{ base: "34px", md: "44px" }}
    h={{ base: "34px", md: "44px" }}
    bg="rgba(255,255,255,0.12)"
    border="1px solid rgba(255,255,255,0.22)"
    borderRadius={{ base: "11px", md: "14px" }}
    align="center"
    justify="center"
    backdropFilter="blur(12px)"
    boxShadow="0 12px 28px rgba(0,0,0,0.22)"
    zIndex={3}
    animation={`${floatOrnament} ${duration} ease-in-out infinite`}
    sx={{ animationDelay: delay, WebkitBackdropFilter: "blur(12px)" }}
  >
    <Icon as={icon} boxSize={{ base: "15px", md: "20px" }} color="white" />
  </Flex>
);

// Scena: pływające ozdoby + dwa telefony (iPhone z lewej, Android z prawej).
const PhoneStage: React.FC<{ iosUrl: string | null; androidUrl: string | null }> = ({ iosUrl, androidUrl }) => (
  <Box position="relative" w="100%" display="flex" justifyContent="center" alignItems="center" minH={{ base: "360px", md: "540px" }}>
    {/* halo */}
    <Box position="absolute" top="50%" left="50%" transform="translate(-50%,-50%)" w={{ base: "320px", md: "560px" }} h={{ base: "320px", md: "560px" }} bg="rgba(255,255,255,0.13)" filter="blur(70px)" borderRadius="full" />
    {/* ozdoby pływające */}
    <Box position="absolute" top="38%" left="0%" w={{ base: "90px", md: "140px" }} h={{ base: "90px", md: "140px" }} borderRadius="full" bg="#8b2fd6" opacity={0.3} filter="blur(46px)" animation={`${floatOrnament} 12s ease-in-out infinite`} />
    <Box position="absolute" bottom="4%" right="2%" w={{ base: "100px", md: "150px" }} h={{ base: "100px", md: "150px" }} borderRadius="full" bg="#E51F44" opacity={0.2} filter="blur(52px)" animation={`${floatOrnament} 13s ease-in-out infinite`} sx={{ animationDelay: "1s" }} />
    {/* pływające ikony-kafelki: portfel, głosowanie, społeczność, blockchain */}
    <FloatBadge icon={FiKey} pos={{ top: "6%", left: "9%" }} duration="7s" delay="0s" />
    <FloatBadge icon={FiCheckCircle} pos={{ top: "12%", right: "7%" }} duration="9s" delay="1.4s" />
    <FloatBadge icon={FiUsers} pos={{ bottom: "10%", left: "6%" }} duration="8s" delay="0.7s" />
    <FloatBadge icon={FiServer} pos={{ bottom: "20%", right: "9%" }} duration="10s" delay="2.1s" />
    {/* drobny akcent */}
    <Box position="absolute" top="44%" left="2%" w="11px" h="11px" borderRadius="full" bg="rgba(255,255,255,0.5)" animation={`${floatOrnament} 6.5s ease-in-out infinite`} sx={{ animationDelay: "1s" }} />

    <Flex position="relative" justify="center" align="center">
      <FloatingPhone
        imageUrl={iosUrl}
        alt="Aplikacja lubbezpośrednio.pl na iPhone"
        transform={{ base: "rotate(-7deg) translateX(20px)", md: "rotate(-8deg) translateX(40px)" }}
        zIndex={1}
        duration="6s"
        delay="0s"
        screen={{
          card: { title: "Budżet obywatelski 2026", rows: [{ label: "Za", pct: 68, color: "#7c4dff" }, { label: "Przeciw", pct: 32, color: "rgba(255,255,255,0.4)" }] },
          donut: { pct: 72, label: "Frekwencja", sub: "1 240 głosów", color: "#7c4dff" },
          stats: ["Kończy się za 2 dni", "Zakotwiczone on-chain"],
          cta: "Zagłosuj"
        }}
      />
      <FloatingPhone
        imageUrl={androidUrl}
        alt="Aplikacja lubbezpośrednio.pl na Androida"
        transform={{ base: "rotate(6deg) translateX(-20px)", md: "rotate(7deg) translateX(-40px)" }}
        zIndex={2}
        duration="6.6s"
        delay="0.9s"
        bgGradient="linear(160deg, #2d0066, #430098)"
        screen={{
          card: { title: "Zarząd wspólnoty — wybór", rows: [{ label: "Kandydat A", pct: 54, color: "#2fce25" }, { label: "Kandydat B", pct: 46, color: "rgba(255,255,255,0.4)" }] },
          donut: { pct: 61, label: "Frekwencja", sub: "860 głosów", color: "#2fce25" },
          stats: ["Tajne i weryfikowalne", "Push o wyniku"],
          cta: "Oddaj głos"
        }}
      />
    </Flex>
  </Box>
);

// ── DUŻA sekcja-hero „Cała platforma w telefonie" (nad filmem) — mockup DWÓCH telefonów (iPhone + Samsung) ──
export const MobileHeroSection: React.FC = () => {
  const settings = usePlatformSettings();

  if (!settings || !settings.app_section_enabled) return null;

  const backend = process.env.REACT_APP_BACKEND_URL ?? "";
  const iosUrl = settings.app_hero_ios_url ? `${backend}${settings.app_hero_ios_url}` : null;
  const androidUrl = settings.app_hero_android_url ? `${backend}${settings.app_hero_android_url}` : null;
  const comingSoon = !settings.app_store_url && !settings.google_play_url;

  return (
    <Box as="section" id="aplikacja" w="100%" position="relative" overflow="hidden" color="white" bgGradient="linear(135deg, #21004a 0%, #430098 50%, #6a1fc2 100%)">
      {/* poświaty / blob-y */}
      <Box position="absolute" inset="0" pointerEvents="none" overflow="hidden">
        <Box position="absolute" top="-140px" left="-90px" w="460px" h="460px" bg="#8b2fd6" opacity={0.4} filter="blur(100px)" borderRadius="full" />
        <Box position="absolute" bottom="-160px" right="-70px" w="520px" h="520px" bg="#E51F44" opacity={0.2} filter="blur(120px)" borderRadius="full" />
        <Box position="absolute" inset="0" bgGradient="radial(circle at 78% 28%, rgba(255,255,255,0.10), transparent 46%)" />
      </Box>

      <Reveal maxW="1260px" mx="auto" px={{ base: "20px", md: "40px", xl: "56px" }} py={{ base: "68px", md: "112px" }} position="relative">
        <Flex direction={{ base: "column", md: "row" }} align="center" gap={{ base: "52px", md: "40px" }}>
          {/* TEKST */}
          <VStack flex={{ md: "1 1 45%" }} align={{ base: "center", md: "flex-start" }} spacing="26px" textAlign={{ base: "center", md: "left" }}>
            <HStack spacing="9px" bg="rgba(255,255,255,0.12)" border="1px solid rgba(255,255,255,0.22)" px="14px" py="6px" borderRadius="full">
              <Icon as={FiSmartphone} boxSize="15px" />
              <Text fontSize="12.5px" fontWeight="800" letterSpacing="0.06em" textTransform="uppercase">
                {comingSoon ? "Aplikacja — już wkrótce" : "Aplikacja mobilna"}
              </Text>
            </HStack>
            <Heading fontSize={{ base: "36px", md: "56px" }} fontWeight="900" lineHeight="1.02" letterSpacing="-1.2px">
              Cała platforma
              <br />w Twoim telefonie
            </Heading>
            <Text fontSize={{ base: "16px", md: "20px" }} color="rgba(255,255,255,0.85)" maxW="520px" lineHeight="1.5">
              {comingSoon
                ? "Głosowania, grupy, społeczność i powiadomienia push — w jednej aplikacji na iPhone'a i Androida. W trakcie weryfikacji w sklepach, już wkrótce do pobrania."
                : "Głosowania, grupy, społeczność i powiadomienia push — w jednej aplikacji na iPhone'a i Androida. Zaloguj się raz i głosuj wygodnie, gdziekolwiek jesteś."}
            </Text>
            <Text fontSize={{ base: "14px", md: "15px" }} color="rgba(255,255,255,0.82)" maxW="520px" lineHeight="1.55">
              W aplikacji żyje Twój <strong>portfel tożsamości</strong> — osobisty klucz, którym podpisujesz logowanie i głos. Nigdy nie opuszcza telefonu (chroniony Face&nbsp;ID / czytnikiem linii papilarnych). Dzięki niemu nikt z samym tokenem nie podszyje się pod Ciebie.
            </Text>
            <HStack spacing="10px" flexWrap="wrap" justify={{ base: "center", md: "flex-start" }}>
              {[
                { icon: FiKey, label: "Portfel tożsamości" },
                { icon: FiShield, label: "Logowanie i głos podpisem" },
                { icon: FiSmartphone, label: "Klucz chroniony biometrią" }
              ].map(c => (
                <HStack key={c.label} spacing="8px" bg="rgba(255,255,255,0.1)" border="1px solid rgba(255,255,255,0.18)" borderRadius="full" px="13px" py="7px">
                  <Icon as={c.icon} boxSize="14px" />
                  <Text fontSize="13px" fontWeight="600">
                    {c.label}
                  </Text>
                </HStack>
              ))}
            </HStack>
            <Flex direction={{ base: "column", sm: "row" }} gap="14px" w={{ base: "100%", sm: "auto" }} maxW={{ base: "320px", sm: "none" }} justify={{ base: "center", md: "flex-start" }} pt="4px">
              <StoreButton icon={FaApple} top="Pobierz w" bottom="App Store" href={settings.app_store_url ?? undefined} />
              <StoreButton icon={FaGooglePlay} top="Pobierz z" bottom="Google Play" href={settings.google_play_url ?? undefined} />
            </Flex>
          </VStack>

          {/* DWA TELEFONY — iPhone + Android, każdy floatuje niezależnie */}
          <Box flex={{ md: "1 1 55%" }} w="100%">
            <PhoneStage iosUrl={iosUrl} androidUrl={androidUrl} />
          </Box>
        </Flex>
      </Reveal>
    </Box>
  );
};

// ── MNIEJSZA sekcja „Pobierz aplikację" (na dole) — własny, mniejszy mockup (app_mockup) ──
export const MobileAppSection: React.FC = () => {
  const settings = usePlatformSettings();

  if (!settings || !settings.app_section_enabled) return null;

  const backend = process.env.REACT_APP_BACKEND_URL ?? "";
  const mockup = settings.app_mockup_url ? `${backend}${settings.app_mockup_url}` : null;
  const comingSoon = !settings.app_store_url && !settings.google_play_url;

  return (
    <Box as="section" w="100%" bgGradient="linear(135deg, #2d0066 0%, #430098 55%, #6a1fc2 100%)" color="white" overflow="hidden">
      <Reveal maxW="1180px" mx="auto" px={{ base: "20px", md: "40px", xl: "56px" }} py={{ base: "48px", md: "72px" }}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: "36px", md: "32px" }} alignItems="center">
          <VStack align={{ base: "center", md: "flex-start" }} spacing="20px" textAlign={{ base: "center", md: "left" }}>
            <Text fontSize="13px" fontWeight="800" letterSpacing="0.08em" textTransform="uppercase" color="rgba(255,255,255,0.7)">
              {comingSoon ? "Już wkrótce" : "Aplikacja mobilna"}
            </Text>
            <Heading fontSize={{ base: "26px", md: "36px" }} fontWeight="800" lineHeight="1.1" letterSpacing="-0.5px">
              Głosuj i twórz głosowania z telefonu
            </Heading>
            <Text fontSize={{ base: "15px", md: "16.5px" }} color="rgba(255,255,255,0.82)" maxW="460px" lineHeight="1.55">
              {comingSoon
                ? "Aplikacja jest w trakcie weryfikacji w sklepach — już wkrótce do pobrania na iOS i Androida."
                : "Cała platforma w kieszeni — głosowania, grupy, społeczność i powiadomienia push. Zaloguj się raz i miej wszystko pod ręką."}
            </Text>
            <Flex direction={{ base: "column", sm: "row" }} gap="14px" w={{ base: "100%", sm: "auto" }} maxW={{ base: "320px", sm: "none" }} justify={{ base: "center", md: "flex-start" }}>
              <StoreButton icon={FaApple} top="Pobierz w" bottom="App Store" href={settings.app_store_url ?? undefined} />
              <StoreButton icon={FaGooglePlay} top="Pobierz z" bottom="Google Play" href={settings.google_play_url ?? undefined} />
            </Flex>
          </VStack>

          {mockup && (
            <Flex justify="center">
              <Image
                src={mockup}
                alt="Aplikacja lubbezpośrednio.pl na telefonie"
                maxH={{ base: "320px", md: "420px" }}
                objectFit="contain"
                filter="drop-shadow(0 28px 56px rgba(0,0,0,0.45))"
              />
            </Flex>
          )}
        </SimpleGrid>
      </Reveal>
    </Box>
  );
};

// ── PANEL PO ZALOGOWANIU (iPad wystający za prawą krawędź, pod filmem) ──
const PanelBullet: React.FC<{ icon: React.ElementType; title: string; desc: string }> = ({ icon, title, desc }) => (
  <HStack align="flex-start" spacing="13px">
    <Flex w="40px" h="40px" borderRadius="12px" bg="rgba(67,0,152,0.08)" align="center" justify="center" flexShrink={0}>
      <Icon as={icon} color="primary" boxSize="19px" />
    </Flex>
    <Box>
      <Text fontWeight="800" color="primary" fontSize="15px">
        {title}
      </Text>
      <Text fontSize="13.5px" color="gray.500" lineHeight="1.45">
        {desc}
      </Text>
    </Box>
  </HStack>
);

const PanelNavItem: React.FC<{ icon: React.ElementType; label: string; active?: boolean }> = ({ icon, label, active }) => (
  <HStack position="relative" spacing="10px" px="11px" py="7.5px" borderRadius="9px" bg={active ? "rgba(67,0,152,0.09)" : "transparent"}>
    {active && <Box position="absolute" left="3px" top="50%" transform="translateY(-50%)" w="3px" h="56%" borderRadius="full" bg="primary" />}
    <Icon as={icon} boxSize="14px" color={active ? "primary" : "gray.400"} />
    <Text fontSize="12px" fontWeight={active ? "700" : "600"} color={active ? "primary" : "gray.500"} whiteSpace="nowrap">
      {label}
    </Text>
  </HStack>
);

const PanelStat: React.FC<{ label: string; value: string; icon: React.ElementType; tint: string }> = ({ label, value, icon, tint }) => (
  <Box bg="white" borderRadius="13px" border="1px solid rgba(67,0,152,0.08)" p="13px" boxShadow="0 4px 14px rgba(67,0,152,0.05)">
    <Flex w="28px" h="28px" borderRadius="9px" bg={tint} align="center" justify="center" mb="9px">
      <Icon as={icon} boxSize="14px" color="primary" />
    </Flex>
    <Text fontSize="19px" fontWeight="800" color="primary" lineHeight="1">
      {value}
    </Text>
    <Text fontSize="10.5px" color="gray.500" mt="3px" noOfLines={1}>
      {label}
    </Text>
  </Box>
);

// Donut frekwencji (jasny motyw panelu) — czysty CSS conic-gradient.
const PanelDonut: React.FC<{ pct: number }> = ({ pct }) => (
  <HStack spacing="12px">
    <Box position="relative" w="66px" h="66px" borderRadius="full" flexShrink={0} style={{ background: `conic-gradient(#7c4dff ${pct}%, #ece7f6 0)` }}>
      <Flex position="absolute" inset="21%" borderRadius="full" bg="white" align="center" justify="center">
        <Text fontSize="14px" fontWeight="800" color="primary">
          {pct}%
        </Text>
      </Flex>
    </Box>
    <Box>
      <HStack spacing="6px">
        <Box w="7px" h="7px" borderRadius="full" bg="#7c4dff" />
        <Text fontSize="10.5px" color="gray.600" fontWeight="600">
          Za · 68%
        </Text>
      </HStack>
      <HStack spacing="6px" mt="4px">
        <Box w="7px" h="7px" borderRadius="full" bg="#ece7f6" />
        <Text fontSize="10.5px" color="gray.500" fontWeight="600">
          Przeciw · 32%
        </Text>
      </HStack>
    </Box>
  </HStack>
);

// Wykres liniowy/area „głosy w czasie".
const PanelTrend: React.FC = () => (
  <Box position="relative" w="100%" h="66px">
    <svg width="100%" height="100%" viewBox="0 0 200 66" preserveAspectRatio="none" style={{ display: "block" }}>
      <defs>
        <linearGradient id="panelTrendFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7c4dff" stopOpacity="0.26" />
          <stop offset="100%" stopColor="#7c4dff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M0,50 L33,40 L66,45 L100,26 L133,31 L166,13 L200,19 L200,66 L0,66 Z" fill="url(#panelTrendFill)" />
      <path d="M0,50 L33,40 L66,45 L100,26 L133,31 L166,13 L200,19" fill="none" stroke="#7c4dff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
    <Box position="absolute" top="13%" left="83%" w="8px" h="8px" borderRadius="full" bg="#7c4dff" boxShadow="0 0 0 3px rgba(124,77,255,0.2)" />
  </Box>
);

const PanelChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box bg="white" borderRadius="12px" border="1px solid rgba(67,0,152,0.08)" p="12px">
    <Text fontSize="9.5px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.04em" mb="11px">
      {title}
    </Text>
    {children}
  </Box>
);

// Poziomy pasek wyniku (Za / Przeciw / Wstrzymanie).
const ResultBar: React.FC<{ label: string; pct: number; color: string }> = ({ label, pct, color }) => (
  <Box>
    <Flex justify="space-between" mb="4px">
      <Text fontSize="11px" color="gray.600" fontWeight="600">
        {label}
      </Text>
      <Text fontSize="11px" color="primary" fontWeight="800">
        {pct}%
      </Text>
    </Flex>
    <Box w="100%" h="8px" bg="#ece7f6" borderRadius="full" overflow="hidden">
      <Box w={`${pct}%`} h="100%" bg={color} borderRadius="full" />
    </Box>
  </Box>
);

const PanelListRow: React.FC<{ title: string; status: string; color: string }> = ({ title, status, color }) => (
  <Flex align="center" justify="space-between">
    <HStack spacing="9px" minW="0">
      <Box w="7px" h="7px" borderRadius="full" bg={color} flexShrink={0} />
      <Text fontSize="11.5px" fontWeight="600" color="gray.700" noOfLines={1}>
        {title}
      </Text>
    </HStack>
    <Text fontSize="10px" fontWeight="700" color="gray.400" whiteSpace="nowrap" pl="8px">
      {status}
    </Text>
  </Flex>
);

export const PanelMock: React.FC = () => (
  <Flex w="100%" h="100%" bg="#F7F5FB">
    {/* sidebar z logo (panel nie ma topbara) */}
    <Flex direction="column" w="174px" p="14px" bg="white" borderRight="1px solid rgba(67,0,152,0.07)" display={{ base: "none", sm: "flex" }} flexShrink={0}>
      <Image src="/LBP_Logo_Black.svg" alt="lubbezpośrednio.pl" h="19px" alignSelf="flex-start" mb="15px" mt="3px" ml="2px" />
      <Flex align="center" justify="center" gap="5px" bg="accent" color="white" borderRadius="11px" py="9px" mb="15px" boxShadow="0 6px 16px rgba(229,31,68,0.32)">
        <Icon as={FiPlus} boxSize="15px" />
        <Text fontSize="12px" fontWeight="800">
          Głosowanie
        </Text>
      </Flex>
      <Text fontSize="8.5px" fontWeight="800" color="gray.400" letterSpacing="0.08em" textTransform="uppercase" mb="7px" ml="4px">
        Menu
      </Text>
      <VStack align="stretch" spacing="3px">
        <PanelNavItem icon={FiGrid} label="Pulpit" active />
        <PanelNavItem icon={FiCheckCircle} label="Głosowania" />
        <PanelNavItem icon={FiUsers} label="Grupy" />
        <PanelNavItem icon={FiMessageSquare} label="Społeczność" />
        <PanelNavItem icon={FiBell} label="Powiadomienia" />
      </VStack>
      <Box flex="1" minH="14px" />
      <HStack spacing="9px" borderTop="1px solid rgba(67,0,152,0.08)" pt="12px">
        <Flex w="30px" h="30px" borderRadius="full" bgGradient="linear(135deg, #430098, #8b2fd6)" align="center" justify="center" flexShrink={0}>
          <Text fontSize="11px" fontWeight="800" color="white">
            F
          </Text>
        </Flex>
        <Box minW="0" flex="1">
          <Text fontSize="11px" fontWeight="800" color="primary" noOfLines={1}>
            Fundacja Potrafisz
          </Text>
          <Text fontSize="9px" color="gray.400">
            Organizator
          </Text>
        </Box>
      </HStack>
    </Flex>
    <Box flex="1" p="16px" overflow="hidden" minW="0">
      <Flex align="center" justify="space-between" mb="12px">
        <Text fontSize="15px" fontWeight="800" color="primary">
          Pulpit
        </Text>
        <Text fontSize="10.5px" color="gray.400" fontWeight="600">
          Ostatnie 30 dni
        </Text>
      </Flex>
      <SimpleGrid columns={4} spacing="9px" mb="11px">
        <PanelStat label="Aktywne" value="3" icon={FiCheckCircle} tint="rgba(67,0,152,0.08)" />
        <PanelStat label="Członkowie" value="142" icon={FiUsers} tint="rgba(124,77,255,0.12)" />
        <PanelStat label="Głosy" value="2 940" icon={FiActivity} tint="rgba(47,206,37,0.14)" />
        <PanelStat label="Grupy" value="6" icon={FiGrid} tint="rgba(229,31,68,0.10)" />
      </SimpleGrid>
      <Flex gap="11px" align="stretch">
        <VStack flex="1.5" align="stretch" spacing="11px" minW="0">
          <Box bg="white" borderRadius="12px" border="1px solid rgba(67,0,152,0.08)" p="13px">
            <Flex justify="space-between" align="center" mb="11px">
              <Text fontSize="12px" fontWeight="800" color="primary" noOfLines={1}>
                Budżet obywatelski 2026
              </Text>
              <HStack spacing="5px" bg="rgba(47,206,37,0.12)" px="8px" py="3px" borderRadius="full" flexShrink={0}>
                <Box w="6px" h="6px" borderRadius="full" bg="#2fce25" />
                <Text fontSize="9px" fontWeight="800" color="#27a91f">
                  NA ŻYWO
                </Text>
              </HStack>
            </Flex>
            <VStack align="stretch" spacing="10px">
              <ResultBar label="Za" pct={68} color="#7c4dff" />
              <ResultBar label="Przeciw" pct={24} color="#b794f4" />
              <ResultBar label="Wstrzymanie" pct={8} color="#d6bcfa" />
            </VStack>
          </Box>
          <PanelChartCard title="Głosy w czasie">
            <PanelTrend />
          </PanelChartCard>
        </VStack>
        <VStack flex="1" align="stretch" spacing="11px" minW="0">
          <PanelChartCard title="Frekwencja">
            <PanelDonut pct={72} />
          </PanelChartCard>
          <Box bg="white" borderRadius="12px" border="1px solid rgba(67,0,152,0.08)" p="13px" flex="1">
            <Text fontSize="9.5px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.04em" mb="10px">
              Ostatnie głosowania
            </Text>
            <VStack align="stretch" spacing="10px">
              <PanelListRow title="Zarząd wspólnoty" status="Zakończone" color="#7c4dff" />
              <PanelListRow title="Regulamin osiedla" status="Zaplanowane" color="#E51F44" />
              <PanelListRow title="Plac zabaw" status="Trwa" color="#2fce25" />
            </VStack>
          </Box>
        </VStack>
      </Flex>
    </Box>
  </Flex>
);

export const IPadFrame: React.FC<BoxProps> = ({ children, ...rest }) => (
  <Box bg="#141414" borderRadius="30px" p="14px" boxShadow="0 50px 110px rgba(20,0,55,0.42)" border="1px solid rgba(0,0,0,0.25)" {...rest}>
    <Box bg="white" borderRadius="18px" overflow="hidden" w="100%" h="100%">
      {children}
    </Box>
  </Box>
);

export const PanelShowcase: React.FC = () => (
  <Box as="section" w="100%" position="relative" overflow="hidden" bg="#F4F1FA" minH={{ lg: "580px" }}>
    {/* floatujące ozdoby */}
    <Box position="absolute" top="14%" right="34%" w={{ base: "80px", md: "130px" }} h={{ base: "80px", md: "130px" }} borderRadius="full" bg="#8b2fd6" opacity={0.16} filter="blur(54px)" animation={`${floatOrnament} 12s ease-in-out infinite`} />
    <Box position="absolute" bottom="8%" right="6%" w={{ base: "100px", md: "160px" }} h={{ base: "100px", md: "160px" }} borderRadius="full" bg="#E51F44" opacity={0.12} filter="blur(64px)" animation={`${floatOrnament} 14s ease-in-out infinite`} sx={{ animationDelay: "1.2s" }} />
    <Box display={{ base: "none", md: "block" }} position="absolute" top="20%" right="41%" w="18px" h="18px" borderRadius="full" border="2px solid rgba(67,0,152,0.22)" animation={`${floatOrnament} 9s ease-in-out infinite`} />
    <Box display={{ base: "none", md: "block" }} position="absolute" bottom="26%" right="3%" w="12px" h="12px" borderRadius="full" bg="rgba(229,31,68,0.4)" animation={`${floatOrnament} 8s ease-in-out infinite`} sx={{ animationDelay: "0.7s" }} />
    <Box display={{ base: "none", md: "block" }} position="absolute" top="34%" right="15%" w="10px" h="10px" borderRadius="full" bg="rgba(124,77,255,0.45)" animation={`${floatOrnament} 10s ease-in-out infinite`} sx={{ animationDelay: "2s" }} />

    {/* tekst (lewa kolumna, w kontenerze) */}
    <Box maxW="1180px" mx="auto" px={{ base: "20px", md: "40px", xl: "56px" }} pt={{ base: "56px", md: "92px" }} pb={{ base: "0", lg: "92px" }} position="relative" zIndex={2}>
      <VStack align={{ base: "center", lg: "flex-start" }} spacing="22px" textAlign={{ base: "center", lg: "left" }} maxW={{ lg: "440px" }}>
        <Text fontSize="13px" fontWeight="800" letterSpacing="0.08em" textTransform="uppercase" color="accent">
          Po zalogowaniu
        </Text>
        <Heading fontSize={{ base: "28px", md: "40px" }} fontWeight="800" color="primary" lineHeight="1.08" letterSpacing="-0.6px">
          Twój panel — wszystko w jednym miejscu
        </Heading>
        <Text fontSize={{ base: "15px", md: "17px" }} color="gray.600" maxW="480px" lineHeight="1.55">
          Twórz i prowadź głosowania, buduj społeczność, zarządzaj grupami i rozmawiaj z członkami — z jednego, przejrzystego panelu.
        </Text>
        <VStack align="stretch" spacing="16px" w="100%" maxW="440px" pt="4px">
          <PanelBullet icon={FiGrid} title="Panel organizatora" desc="Pulpit z wynikami, statusami i frekwencją w czasie rzeczywistym." />
          <PanelBullet icon={FiMessageSquare} title="Społeczność" desc="Wspólna przestrzeń — posty, reakcje, dyskusje wokół Twoich głosowań." />
          <PanelBullet icon={FiUsers} title="Grupy" desc="Twórz grupy, zapraszaj członków i prowadź dla nich zamknięte głosowania." />
        </VStack>
        <Button as={Link} to="/rejestracja" bg="primary" color="white" borderRadius="14px" px="26px" h="52px" fontWeight="700" fontSize="16px" rightIcon={<FiArrowRight />} _hover={{ bg: "#5a13b0" }}>
          Załóż konto za darmo
        </Button>
      </VStack>
    </Box>

    {/* iPad — desktop: wychodzi za prawą krawędź (Apple-style), skaluje się z vw */}
    <IPadFrame display={{ base: "none", lg: "block" }} position="absolute" top="50%" left="54%" transform="translateY(-50%)" w="min(50vw, 860px)" minW="700px" zIndex={1} sx={{ aspectRatio: "1.72" }}>
      <PanelMock />
    </IPadFrame>

    {/* iPad — mobile/tablet: landscape, wystaje za prawą krawędź (jak na desktopie), nie kwadrat */}
    <Box display={{ base: "block", lg: "none" }} px={{ base: "20px", md: "40px" }} pt={{ base: "36px", md: "52px" }} pb={{ base: "52px", md: "80px" }} position="relative" zIndex={2}>
      <IPadFrame w={{ base: "560px", md: "680px" }} maxW="none" sx={{ aspectRatio: "1.55" }}>
        <PanelMock />
      </IPadFrame>
    </Box>
  </Box>
);

// ── EKSPLORER (publiczny eksplorer blockchaina) ──
const ExplorerRow: React.FC<{ type: string; ref_: string; tint: string }> = ({ type, ref_, tint }) => (
  <Flex align="center" justify="space-between" gap="10px" bg="rgba(255,255,255,0.05)" border="1px solid rgba(255,255,255,0.09)" borderRadius="12px" px="13px" py="11px">
    <HStack spacing="11px" minW="0">
      <Flex w="30px" h="30px" borderRadius="9px" bg={tint} align="center" justify="center" flexShrink={0}>
        <Icon as={FiBox} boxSize="15px" color="white" />
      </Flex>
      <Box minW="0">
        <Text fontSize="12.5px" fontWeight="700" color="white" noOfLines={1}>
          {type}
        </Text>
        <Text fontFamily="mono" fontSize="11px" color="rgba(255,255,255,0.5)" noOfLines={1}>
          {ref_}
        </Text>
      </Box>
    </HStack>
    <HStack spacing="5px" bg="rgba(47,206,37,0.16)" px="9px" py="4px" borderRadius="full" flexShrink={0}>
      <Icon as={FiCheckCircle} boxSize="11px" color="#2fce25" />
      <Text fontSize="10.5px" fontWeight="700" color="#7ee87a" whiteSpace="nowrap">
        Zakotwiczone
      </Text>
    </HStack>
  </Flex>
);

const ZkPoint: React.FC<{ icon: React.ElementType; title: string; desc: string }> = ({ icon, title, desc }) => (
  <VStack align="flex-start" spacing="11px" bg="rgba(255,255,255,0.05)" border="1px solid rgba(255,255,255,0.1)" borderRadius="18px" p={{ base: "20px", md: "24px" }} h="100%">
    <Flex w="44px" h="44px" borderRadius="13px" bg="rgba(255,255,255,0.1)" align="center" justify="center">
      <Icon as={icon} boxSize="21px" color="#c4a8ff" />
    </Flex>
    <Text fontSize={{ base: "17px", md: "18px" }} fontWeight="800" color="white">
      {title}
    </Text>
    <Text fontSize={{ base: "14px", md: "15px" }} color="rgba(255,255,255,0.72)" lineHeight="1.6">
      {desc}
    </Text>
  </VStack>
);

// Trójkąt 3 niezależnych węzłów połączonych liniami — wizualizacja rozproszonego 2-z-3 (białe tło).
const TriNode: React.FC<{ label: string; az: string; pos: { left: string; top: string } }> = ({ label, az, pos }) => (
  <VStack position="absolute" left={pos.left} top={pos.top} transform="translate(-50%, -50%)" spacing="6px" zIndex={2}>
    <Flex w={{ base: "54px", md: "64px" }} h={{ base: "54px", md: "64px" }} borderRadius="17px" bg="white" border="1px solid rgba(67,0,152,0.12)" boxShadow="0 10px 26px rgba(67,0,152,0.14)" align="center" justify="center">
      <Icon as={FiServer} boxSize={{ base: "25px", md: "29px" }} color="primary" />
    </Flex>
    <VStack spacing="0">
      <Text fontSize="12px" fontWeight="800" color="primary">
        {label}
      </Text>
      <Text fontSize="10px" color="gray.500" fontWeight="600">
        {az}
      </Text>
    </VStack>
  </VStack>
);

const TrustTriangle: React.FC = () => (
  <Box position="relative" w="100%" maxW="440px" mx="auto" sx={{ aspectRatio: "1 / 0.92" }}>
    <Box as="svg" viewBox="0 0 100 92" position="absolute" inset="0" w="100%" h="100%" overflow="visible">
      <line x1="50" y1="12" x2="14" y2="76" stroke="rgba(67,0,152,0.22)" strokeWidth="1.1" />
      <line x1="14" y1="76" x2="86" y2="76" stroke="rgba(67,0,152,0.22)" strokeWidth="1.1" />
      <line x1="86" y1="76" x2="50" y2="12" stroke="rgba(67,0,152,0.22)" strokeWidth="1.1" />
      <line x1="50" y1="12" x2="50" y2="46" stroke="#2fce25" strokeWidth="1.5" />
      <line x1="14" y1="76" x2="50" y2="46" stroke="#2fce25" strokeWidth="1.5" />
      <line x1="86" y1="76" x2="50" y2="46" stroke="rgba(67,0,152,0.25)" strokeWidth="1.2" strokeDasharray="3 3" />
    </Box>
    <VStack position="absolute" left="50%" top="50%" transform="translate(-50%, -50%)" spacing="5px" bg="white" border="1px solid rgba(67,0,152,0.12)" boxShadow="0 12px 30px rgba(67,0,152,0.16)" borderRadius="14px" px="14px" py="10px" zIndex={2}>
      <Text fontSize="11px" fontFamily="mono" color="gray.500" fontWeight="700">
        blok #2 940
      </Text>
      <HStack spacing="5px" bg="rgba(47,206,37,0.12)" px="9px" py="3px" borderRadius="full">
        <Icon as={FiCheckCircle} boxSize="11px" color="#2fce25" />
        <Text fontSize="11px" fontWeight="800" color="#1f9e18">
          podpisany 2 z 3
        </Text>
      </HStack>
    </VStack>
    <TriNode label="Węzeł 1" az="strefa 1a" pos={{ left: "50%", top: "13%" }} />
    <TriNode label="Węzeł 2" az="strefa 1b" pos={{ left: "14%", top: "82%" }} />
    <TriNode label="Węzeł 3" az="strefa 1c" pos={{ left: "86%", top: "82%" }} />
  </Box>
);

const ChainPoint: React.FC<{ icon: React.ElementType; title: string; desc: string }> = ({ icon, title, desc }) => (
  <HStack align="flex-start" spacing="14px">
    <Flex w="44px" h="44px" borderRadius="12px" bg="rgba(67,0,152,0.07)" align="center" justify="center" flexShrink={0}>
      <Icon as={icon} color="primary" boxSize="21px" />
    </Flex>
    <Box>
      <Text fontWeight="800" color="primary" fontSize={{ base: "16px", md: "17px" }} mb="3px">
        {title}
      </Text>
      <Text color="gray.600" fontSize={{ base: "14px", md: "15px" }} lineHeight="1.6">
        {desc}
      </Text>
    </Box>
  </HStack>
);

// Sekcja: rozproszony łańcuch 2-z-3 (białe tło) — po lewej tekst + karty, po prawej trójkąt węzłów.
export const DistributedChainSection: React.FC = () => (
  <Box as="section" w="100%" bg="white" position="relative" overflow="hidden">
    <Reveal maxW="1180px" mx="auto" px={{ base: "20px", md: "40px", xl: "56px" }} py={{ base: "60px", md: "92px" }}>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: "52px", lg: "64px" }} alignItems="center">
        <VStack align={{ base: "center", lg: "flex-start" }} spacing="20px" textAlign={{ base: "center", lg: "left" }}>
          <HStack spacing="9px" bg="rgba(67,0,152,0.06)" px="14px" py="6px" borderRadius="full">
            <Icon as={FiServer} boxSize="14px" color="primary" />
            <Text fontSize="12.5px" fontWeight="800" letterSpacing="0.05em" textTransform="uppercase" color="primary">
              Rozproszony łańcuch
            </Text>
          </HStack>
          <Heading fontSize={{ base: "30px", md: "44px" }} fontWeight="900" lineHeight="1.07" letterSpacing="-0.8px" color="primary">
            Żaden pojedynczy serwer nie kontroluje łańcucha
          </Heading>
          <Text fontSize={{ base: "16px", md: "18px" }} color="gray.600" lineHeight="1.65" maxW="520px">
            Łańcuch prowadzą trzy niezależne węzły w osobnych strefach. Każdy blok musi podpisać co najmniej dwa z nich —
            żaden serwer, nawet nasz, nie zmieni wyniku w pojedynkę, a awaria jednego węzła nie zatrzymuje głosowania.
          </Text>
          <VStack align="stretch" spacing="18px" w="100%" maxW="520px" pt="4px">
            <ChainPoint icon={FiServer} title="Trzy niezależne węzły" desc="Łańcuch nie stoi na jednym serwerze — prowadzą go trzy oddzielne węzły w różnych strefach. Żaden sam nie ma władzy." />
            <ChainPoint icon={FiCheckCircle} title="Każdy blok podpisany 2 z 3" desc="Zapis jest ważny dopiero, gdy zgodzą się co najmniej dwa węzły. Pojedynczy podmiot nic nie dopisze ani nie podmieni." />
            <ChainPoint icon={FiShield} title="Odporny na awarię" desc="Gdy jeden węzeł padnie lub zostanie przejęty, dwa pozostałe działają dalej — głosowanie trwa nieprzerwanie." />
          </VStack>
        </VStack>
        <TrustTriangle />
      </SimpleGrid>
    </Reveal>
  </Box>
);

const blink = keyframes`50% { opacity: 0; }`;

// Wiersz korzyści (ikona + tekst) na ciemnym tle sekcji „własny węzeł".
const NodeBenefit: React.FC<{ icon: React.ElementType; title: string; desc: string }> = ({ icon, title, desc }) => (
  <HStack align="flex-start" spacing="14px">
    <Flex w="42px" h="42px" borderRadius="11px" bg="rgba(255,255,255,0.1)" border="1px solid rgba(255,255,255,0.16)" align="center" justify="center" flexShrink={0}>
      <Icon as={icon} color="white" boxSize="19px" />
    </Flex>
    <Box>
      <Text fontWeight="800" color="white" fontSize={{ base: "15px", md: "16px" }} mb="2px">
        {title}
      </Text>
      <Text color="rgba(255,255,255,0.7)" fontSize={{ base: "13.5px", md: "14.5px" }} lineHeight="1.55">
        {desc}
      </Text>
    </Box>
  </HStack>
);

// Sekcja (ciemna, pod 2-z-3): każdy może postawić własny węzeł i NIEZALEŻNIE zweryfikować cały łańcuch.
// Link do repo (GitHub) sterowany z /admin → Ustawienia platformy (node_repo_url). Pusty = brak przycisku.
export const OwnNodeSection: React.FC = () => {
  const settings = usePlatformSettings();
  const repoUrl = settings?.node_repo_url ?? undefined;
  return (
  <Box as="section" id="wlasny-wezel" w="100%" position="relative" overflow="hidden" color="white" bgGradient="linear(150deg, #1b0040 0%, #2d0066 55%, #430098 100%)">
    <Box position="absolute" inset="0" pointerEvents="none">
      <Box position="absolute" top="-120px" right="-80px" w="420px" h="420px" bg="#8b2fd6" opacity={0.28} filter="blur(110px)" borderRadius="full" />
      <Box position="absolute" bottom="-140px" left="-60px" w="380px" h="380px" bg="#E51F44" opacity={0.16} filter="blur(120px)" borderRadius="full" />
    </Box>
    <Reveal maxW="1180px" mx="auto" px={{ base: "20px", md: "40px", xl: "56px" }} py={{ base: "60px", md: "92px" }} position="relative">
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: "44px", lg: "64px" }} alignItems="center">
        <VStack align={{ base: "center", lg: "flex-start" }} spacing="22px" textAlign={{ base: "center", lg: "left" }}>
          <HStack spacing="9px" bg="rgba(255,255,255,0.1)" border="1px solid rgba(255,255,255,0.2)" px="14px" py="6px" borderRadius="full">
            <Icon as={FiBox} boxSize="14px" />
            <Text fontSize="12.5px" fontWeight="800" letterSpacing="0.05em" textTransform="uppercase">
              Otwarty kod · AGPLv3
            </Text>
          </HStack>
          <Heading fontSize={{ base: "30px", md: "44px" }} fontWeight="900" lineHeight="1.07" letterSpacing="-0.8px">
            Nie musisz nam wierzyć —<br />sprawdź sam
          </Heading>
          <Text fontSize={{ base: "16px", md: "18px" }} color="rgba(255,255,255,0.82)" lineHeight="1.6" maxW="520px">
            Kod węzła jest otwarty. Każdy może postawić <strong>własny węzeł</strong> na Linuksie, ściągnąć cały
            łańcuch i niezależnie zweryfikować każdy blok oraz każdy dowód głosowania — bez polegania na nas.
          </Text>
          <VStack align="stretch" spacing="16px" w="100%" maxW="520px" pt="2px">
            <NodeBenefit icon={FiServer} title="Postaw węzeł jednym skryptem" desc="Autoinstalator na Debianie/Ubuntu uruchamia pełny węzeł, który synchronizuje i trzyma całą historię łańcucha." />
            <NodeBenefit icon={FiShield} title="Weryfikujesz wszystko sam" desc="Twój węzeł sprawdza podpisy 2-z-3 i dowody ZK lokalnie — odrzuci każdy blok, który się nie zgadza." />
            <NodeBenefit icon={FiSearch} title="Własny eksplorer" desc="Na swoim węźle postawisz własny podgląd łańcucha — zero zaufania do naszych serwerów." />
          </VStack>
          {repoUrl && (
            <HStack as="a" href={repoUrl} target="_blank" rel="noopener noreferrer" spacing="9px" bg="white" color="#2d0066" px="22px" py="13px" borderRadius="12px" fontWeight="800" fontSize="15px" transition="all 0.15s" _hover={{ transform: "translateY(-2px)", boxShadow: "0 16px 34px rgba(0,0,0,0.32)" }}>
              <Icon as={FiBox} boxSize="17px" />
              <Text>Zobacz kod na GitHubie</Text>
              <Icon as={FiArrowRight} boxSize="16px" />
            </HStack>
          )}
        </VStack>

        {/* terminal — pokazuje, że to realnie działa */}
        <Box w="100%" bg="rgba(8,0,20,0.55)" border="1px solid rgba(255,255,255,0.14)" borderRadius="18px" overflow="hidden" boxShadow="0 24px 60px rgba(0,0,0,0.4)" backdropFilter="blur(6px)">
          <HStack spacing="7px" px="16px" py="12px" borderBottom="1px solid rgba(255,255,255,0.1)">
            <Box w="11px" h="11px" borderRadius="full" bg="#ff5f57" />
            <Box w="11px" h="11px" borderRadius="full" bg="#febc2e" />
            <Box w="11px" h="11px" borderRadius="full" bg="#28c840" />
            <Text fontSize="12px" color="rgba(255,255,255,0.4)" pl="8px" fontFamily="mono">
              twój-węzeł — Linux
            </Text>
          </HStack>
          <Box p={{ base: "18px", md: "22px" }} fontFamily="mono" fontSize={{ base: "12.5px", md: "13.5px" }} lineHeight="1.85">
            <Text color="rgba(255,255,255,0.55)"><Text as="span" color="#2FCE25">$</Text> sudo ./install-node.sh</Text>
            <Text color="rgba(255,255,255,0.6)">▶ build węzła + weryfikatora ZK…</Text>
            <Text color="rgba(255,255,255,0.6)">▶ łączenie z siecią…</Text>
            <Text color="#2FCE25">✔ węzeł zsynchronizowany z łańcuchem</Text>
            <Box h="10px" />
            <Text color="rgba(255,255,255,0.55)"><Text as="span" color="#2FCE25">$</Text> lbz-node-cli height</Text>
            <Text color="rgba(255,255,255,0.85)">{`{ "height": 1, "status": "done" }`}</Text>
            <Text color="rgba(255,255,255,0.55)"><Text as="span" color="#2FCE25">$</Text> <Text as="span" sx={{ animation: `${blink} 1.1s step-end infinite` }}>▋</Text></Text>
          </Box>
        </Box>
      </SimpleGrid>
    </Reveal>
  </Box>
  );
};

// Jedna sekcja na wspólnym ciemnym tle: NAD eksplorerem historia ZKP (tajne + weryfikowalne),
// pod spodem podgląd publicznego eksploratora — razem: tajność + jawny dowód + zero zaufania.
export const ExplorerShowcase: React.FC = () => (
  <Box as="section" w="100%" position="relative" overflow="hidden" color="white" bgGradient="linear(135deg, #16002f 0%, #2d0066 55%, #430098 100%)">
    <Box position="absolute" inset="0" pointerEvents="none" overflow="hidden">
      <Box position="absolute" top="-120px" right="-80px" w="420px" h="420px" bg="#8b2fd6" opacity={0.3} filter="blur(100px)" borderRadius="full" />
      <Box position="absolute" bottom="-140px" left="-70px" w="460px" h="460px" bg="#2fce25" opacity={0.12} filter="blur(120px)" borderRadius="full" />
    </Box>
    <Reveal maxW="1180px" mx="auto" px={{ base: "20px", md: "40px", xl: "56px" }} py={{ base: "64px", md: "96px" }} position="relative">
      {/* ── ZKP: tajne i weryfikowalne ── */}
      <VStack spacing={{ base: "16px", md: "20px" }} textAlign="center" maxW="800px" mx="auto" mb={{ base: "36px", md: "48px" }}>
        <HStack spacing="9px" bg="rgba(255,255,255,0.10)" border="1px solid rgba(255,255,255,0.2)" px="14px" py="6px" borderRadius="full">
          <Icon as={FiEyeOff} boxSize="14px" />
          <Text fontSize="12.5px" fontWeight="800" letterSpacing="0.05em" textTransform="uppercase">
            Tajne i weryfikowalne
          </Text>
        </HStack>
        <Heading fontSize={{ base: "30px", md: "46px" }} fontWeight="900" lineHeight="1.05" letterSpacing="-0.8px">
          Twój głos jest tajny — i mimo to policzalny
        </Heading>
        <Text fontSize={{ base: "16px", md: "19px" }} color="rgba(255,255,255,0.82)" lineHeight="1.6">
          Dzięki dowodom z wiedzą zerową (ZKP) udowodnisz, że Twój głos jest ważny i policzony, a mimo to nikt — łącznie z
          nami — nie dowie się, jak zagłosowałeś. Nie musisz nikomu ufać: gwarantuje to matematyka, nie obietnica.
        </Text>
      </VStack>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: "16px", md: "22px" }} mb={{ base: "28px", md: "36px" }}>
        <ZkPoint icon={FiEyeOff} title="Tajny" desc="Twoja tożsamość i Twój wybór nigdy nie spotykają się w jednym miejscu. Nikt nie powiąże głosu z Tobą." />
        <ZkPoint icon={FiShield} title="Weryfikowalny" desc="Dowód potwierdza, że jesteś uprawniony i głosujesz raz — nie ujawniając, którym z uprawnionych jesteś." />
        <ZkPoint icon={FiKey} title="Bez zaufania" desc="Uczciwość wyniku gwarantuje kryptografia. Każdy może go niezależnie przeliczyć i sprawdzić." />
      </SimpleGrid>

      <Flex justify="center" gap="12px" wrap="wrap" mb={{ base: "52px", md: "72px" }}>
        <Button as={Link} to="/tajne-glosowanie-zkp" variant="outline" borderColor="rgba(255,255,255,0.35)" color="white" borderRadius="14px" px="24px" h="48px" fontWeight="800" fontSize="15px" rightIcon={<FiArrowRight />} _hover={{ bg: "rgba(255,255,255,0.1)", textDecoration: "none" }} transition="all 0.15s">
          Jak to działa
        </Button>
        <Button as={Link} to="/tozsamosc-i-bezpieczenstwo" variant="outline" borderColor="rgba(255,255,255,0.35)" color="white" borderRadius="14px" px="24px" h="48px" fontWeight="800" fontSize="15px" rightIcon={<FiArrowRight />} _hover={{ bg: "rgba(255,255,255,0.1)", textDecoration: "none" }} transition="all 0.15s">
          Tożsamość i bezpieczeństwo
        </Button>
      </Flex>

      <Box h="1px" bgGradient="linear(to-r, transparent, rgba(255,255,255,0.18), transparent)" mb={{ base: "52px", md: "72px" }} />

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: "44px", lg: "48px" }} alignItems="center">
        <VStack align={{ base: "center", lg: "flex-start" }} spacing="22px" textAlign={{ base: "center", lg: "left" }}>
          <HStack spacing="9px" bg="rgba(255,255,255,0.10)" border="1px solid rgba(255,255,255,0.2)" px="14px" py="6px" borderRadius="full">
            <Icon as={FiSearch} boxSize="14px" />
            <Text fontSize="12.5px" fontWeight="800" letterSpacing="0.05em" textTransform="uppercase">
              Publiczny eksplorer
            </Text>
          </HStack>
          <Heading fontSize={{ base: "30px", md: "44px" }} fontWeight="900" lineHeight="1.05" letterSpacing="-0.8px">
            Sprawdź każdy wynik na łańcuchu
          </Heading>
          <Text fontSize={{ base: "16px", md: "19px" }} color="rgba(255,255,255,0.85)" maxW="500px" lineHeight="1.5">
            Każdy zakończony wynik jest zakotwiczony na blockchainie. W otwartym eksplorerze prześledzisz transakcje, bloki i odciski — bez logowania, w pełni publicznie.
          </Text>
          <Button as="a" href="https://eksplorer.lubbezposrednio.pl" target="_blank" rel="noopener noreferrer" bg="white" color="primary" borderRadius="14px" px="26px" h="52px" fontWeight="800" fontSize="16px" rightIcon={<FiArrowRight />} _hover={{ bg: "rgba(255,255,255,0.9)", transform: "translateY(-2px)", textDecoration: "none" }} transition="all 0.15s">
            Otwórz eksplorer
          </Button>
        </VStack>

        <Box bg="rgba(255,255,255,0.04)" border="1px solid rgba(255,255,255,0.12)" borderRadius="22px" p={{ base: "16px", md: "20px" }} backdropFilter="blur(8px)" boxShadow="0 30px 70px rgba(0,0,0,0.35)">
          <Flex align="center" justify="space-between" mb="14px">
            <HStack spacing="9px">
              <Icon as={FiLink} boxSize="16px" color="#a78bfa" />
              <Text fontSize="13px" fontWeight="800">
                Ostatnie kotwice
              </Text>
            </HStack>
            <Text fontSize="11px" color="rgba(255,255,255,0.5)" fontFamily="mono">
              blok #2 940
            </Text>
          </Flex>
          <VStack align="stretch" spacing="10px">
            <ExplorerRow type="Wynik głosowania" ref_="@PL-V-26-346ac108" tint="rgba(124,77,255,0.35)" />
            <ExplorerRow type="Wynik głosowania" ref_="@PL-V-26-9f2ac41e" tint="rgba(124,77,255,0.35)" />
            <ExplorerRow type="Profil zweryfikowany" ref_="@PL-U-26-7b08e3d6" tint="rgba(47,206,37,0.3)" />
          </VStack>
        </Box>
      </SimpleGrid>
    </Reveal>
  </Box>
);
